import React, {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import {ClickOutsideDetect} from '@montana/shared/click-outside-detect';
import {TabletLandscapeAndLarger, SmallerThanTabletLandscape} from '@montana/shared/responsive';
import {usePDPContext} from '../../product-page.context';
import cx from 'clsx';
import {AnimatePresence, motion} from 'framer-motion';
import {IVariation} from './variation';
import {VariationDesktopButton} from './variation-button.component';
import {useTranslations} from '@montana/containers/translations';
import {SvgIcon} from '@montana/shared/svg-icon';


export interface IVariationsWrapper {
    variations?: IVariation[];
    mobileLayout?: boolean;
}

export const VariationsWrapper: React.FC<IVariationsWrapper> = React.memo(({
    variations,
    mobileLayout = false,
}: IVariationsWrapper): JSX.Element => {
    const { activeVariationPicker, setActiveVariationPicker } = usePDPContext();
    const [shownVariationPicker, setShownVariationPicker] = useState<{ component: ReactElement | undefined; dimensionName: string } | null | undefined>();
    const [contentTopPos, setContentTopPos] = useState<number>(0);

    const translate = useTranslations();

    // Calculates the css top position of the variations content layer
    const variationsElementRef = useCallback((node: HTMLDivElement) => {
        if (node) {
            const parentElemHeight = node.offsetParent?.clientHeight ?? 0;
            const refElemBottomPadding =  +getComputedStyle(node).paddingBottom.replace('px','');
            const refElemHeight = node.offsetHeight + refElemBottomPadding + 16;
            setContentTopPos((parentElemHeight - refElemHeight) / 2);
        }
    },[activeVariationPicker, shownVariationPicker])

    const arrayOfShownVariationPicker = useMemo(() => {
        if (shownVariationPicker && shownVariationPicker.component) {
            return [shownVariationPicker];
        }
        return [];
    }, [shownVariationPicker]);

    useEffect(() => {
        if (!!activeVariationPicker) {
            const variantPickerToShow = variations?.find(v => v.dimensionName.toLowerCase() === activeVariationPicker.toLowerCase());
            if (variantPickerToShow && shownVariationPicker?.dimensionName !== activeVariationPicker) {
                setShownVariationPicker({ component: variantPickerToShow.component, dimensionName: variantPickerToShow.dimensionName });
            }
        } else {
            setShownVariationPicker(null);
        }
    }, [activeVariationPicker]);

    const handleClick = (dimensionName?: string) => {
        if (activeVariationPicker === dimensionName) {
            setActiveVariationPicker();
        } else {
            setActiveVariationPicker(dimensionName);
        }
    };


    return (
        <div className={cx('variations-wrapper', {
            'variations-wrapper--open': !!shownVariationPicker,
            'variations-wrapper--mobile': !!mobileLayout,
        })}>
            <ClickOutsideDetect onClickOutside={() => !!shownVariationPicker && setActiveVariationPicker()}>
                <TabletLandscapeAndLarger>
                    <AnimatePresence exitBeforeEnter={true}>
                        { arrayOfShownVariationPicker
                            .map((pickerItem) => (
                                <motion.div
                                    className="variations-wrapper__content"
                                    style={{'top': `${contentTopPos}px`}}
                                    key={pickerItem?.dimensionName}
                                    transition={{
                                        duration: 0.1,
                                        ease: [0.37, 0, 0.2, 1]
                                    }}
                                    initial={{ x: -5, opacity: 0 }}
                                    animate={{ x: 0, opacity: 1 }}
                                    exit={{ x: 5, opacity: 0 }}
                                >
                                    {pickerItem?.component}
                                </motion.div>
                            ))}
                    </AnimatePresence>
                </TabletLandscapeAndLarger>

                <div className="variation-button-scroll-wrapper" ref={variationsElementRef}>
                    <div className={cx('variation-button-wrapper', {
                        'variation-button-wrapper--mobile': !!mobileLayout,
                        'variation-button-wrapper--scroll': !!variations && (variations?.length - 1) >= 5 && !!mobileLayout
                    })}>
                        {variations?.map((v, index) => (
                            <React.Fragment key={v.dimensionName}>
                                <SmallerThanTabletLandscape includeSSR>
                                    {v.dimensionName !== 'Zoom' &&
                                        <div className={cx('variation-button__wrap', {
                                            'variation-button__wrap--spacing': v.spacing,
                                            'variation-button__wrap--mobile': !!mobileLayout,
                                            'variation-button__wrap--mobile-last': index + 1 === variations?.length - 1,
                                        })}>
                                            <button
                                                onClick={v.clickHandler ? v.clickHandler : () => { handleClick(v.dimensionName); }}
                                                className={cx('variation-button', {
                                                    'variation-button--active': v.dimensionName === shownVariationPicker?.dimensionName,
                                                    'variation-button--mobile': !!mobileLayout,
                                                })}
                                                type="button">
                                                {v.dimensionName === shownVariationPicker?.dimensionName ? <SvgIcon className="variations-wrapper__icon" iconName="close" /> : v.buttonIcon}
                                                <span>{v.buttonText}</span>
                                            </button>
                                        </div>
                                    }
                                    {v.dimensionName === 'Zoom' &&
                                        <div className="zoom">
                                            <button
                                                onClick={v.clickHandler ? v.clickHandler : () => { handleClick(v.dimensionName); }}
                                                className="zoom__button"
                                                type="button">
                                                <span>{translate((t: any) => t.productPage.dimensionMobileZoom, '[Tap to zoom]')}</span>
                                            </button>
                                        </div>
                                    }
                                </SmallerThanTabletLandscape>
                                <TabletLandscapeAndLarger>
                                    {v.dimensionName !== 'Zoom' &&
                                        <VariationDesktopButton
                                            {...v}
                                            clickHandler={v.clickHandler ? v.clickHandler : () => { handleClick(v.dimensionName); }}
                                            isActive={v.dimensionName === shownVariationPicker?.dimensionName} />
                                    }
                                </TabletLandscapeAndLarger>

                            </React.Fragment>
                        ))}
                    </div>
                    {variations?.map(v => (
                        <React.Fragment key={v.dimensionName}>
                            <SmallerThanTabletLandscape includeSSR>
                                {v.dimensionName === shownVariationPicker?.dimensionName && (
                                    <div className="variations-wrapper__content">
                                        {shownVariationPicker?.component}
                                    </div>
                                )}
                            </SmallerThanTabletLandscape>
                        </React.Fragment>
                    ))}
                </div>
            </ClickOutsideDetect>
        </div>
    );

});
